<template>
  <template v-if="requiresTarget">
    <PuppyFormItem>
      <template #icon><span>{{ targetLabel }}</span></template>
      <PuppyInputSelect
        :items="item ? [item] : []"
        :active="active"
        :active-index="active && item ? 0 : null"
        :selected-indices="active && item ? [0] : []"
        :visible-rows="1"
        :empty-text="`选择${targetLabel}`"
        @update:active="emit('activate', $event)"
        @item-click="emit('visual', 'select')"
        @delete-item="removeTarget"
        @delete-all="removeTarget"
      >
        <template #item>
          <span class="block w-full" @mouseenter="emit('visual', 'hover')" @mouseleave="emit('visual', 'clear')">{{ item?.label }}</span>
        </template>
      </PuppyInputSelect>
    </PuppyFormItem>
    <template v-if="direction.endCondition === 'offset_from_surface'">
      <PuppyFormItem>
        <template #icon><span>距离</span></template>
        <n-input-group>
          <n-input-number :value="convertMToMm(direction.offset ?? 0.01)" :min="0" :precision="3" size="small" @update:value="updateOffset" />
          <n-input-group-label size="small">mm</n-input-group-label>
        </n-input-group>
      </PuppyFormItem>
      <n-checkbox :checked="direction.reverseOffset === true" @update:checked="emit('update:direction', { ...direction, reverseOffset: $event })">反向等距</n-checkbox>
    </template>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { NCheckbox, NInputGroup, NInputGroupLabel, NInputNumber } from 'naive-ui';
import { convertMToMm, convertMmToM } from '@puppycad/cad-core';
import type { ExtrudeDirectionDefinition, ExtrudeEndTargetDisplay } from '@puppycad/cad-core';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';

const props = defineProps<{ direction: ExtrudeDirectionDefinition; active: boolean; item?: ExtrudeEndTargetDisplay }>();
const emit = defineEmits<{ 'update:direction': [value: ExtrudeDirectionDefinition]; activate: [active: boolean]; visual: [mode: 'select' | 'hover' | 'clear'] }>();
const requiresTarget = computed(() => ['up_to_vertex', 'up_to_surface', 'offset_from_surface', 'up_to_body'].includes(props.direction.endCondition));
const targetLabel = computed(() => props.direction.endCondition === 'up_to_vertex' ? '顶点' : props.direction.endCondition === 'up_to_body' ? '实体或曲面体' : '面');

/** 删除仅清理当前方向目标，先撤销临时高亮，保留其余正式参数。 */
function removeTarget(): void {
  emit('visual', 'clear');
  emit('update:direction', { ...props.direction, target: null });
}

/**
 * 将毫米偏移写回 SDK 定义；零偏移合法，不沿用给定深度的正数下限。
 * @param value 输入毫米值。
 */
function updateOffset(value: number | null): void {
  if (value === null || !Number.isFinite(value)) return;
  emit('update:direction', { ...props.direction, offset: convertMmToM(Math.max(0, value)) });
}
</script>
